import {faShoppingCart} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import React, {useEffect, useState, useRef} from 'react'
import {createPortal} from 'react-dom'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {
  selectCartItemCount,
  showCart
} from '../../store/slices/shoppingCartSlice'
import {getLogger} from '../../services/logging'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('ShoppingCartButton')

export interface ShoppingCartButtonProps {
  className?: string
  targetSelector?: string // New prop for portal target
}

const ShoppingCartButton = ({
  className,
  targetSelector
}: ShoppingCartButtonProps) => {
  const dispatch = useAppDispatch()
  const cartItemCount = useAppSelector(selectCartItemCount)
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
  const portalCreatedRef = useRef(false)

  // Effect to find the target element for portal rendering
  useEffect(() => {
    if (!targetSelector) {
      return
    }

    const element = document.querySelector(targetSelector) as HTMLElement
    if (element && !portalCreatedRef.current) {
      // Set up the element before setting it as targetElement
      element.innerHTML = ''
      element.classList.add('placemakr-ibe')
      element.setAttribute('data-theme', 'placemakr')
      portalCreatedRef.current = true
      setTargetElement(element)
    } else if (element && portalCreatedRef.current) {
      // Element already set up, just update the state
      setTargetElement(element)
    } else {
      console.log(`Target element not found for selector: ${targetSelector}`)
    }
  }, [targetSelector])

  const CartButton = (
    <button
      className={clsx(
        'btn btn-outline btn-sm lg:btn-md flex items-center',
        className
      )}
      onClick={() => {
        logger.debug({message: 'Show shopping cart button clicked.'})

        pushDataLayerEvent({
          event: 'Shopping_Cart_Button_Click',
          category: 'IBE',
          value: 1
        })
        dispatch(showCart())
      }}
      aria-label="Shopping cart button."
    >
      <FontAwesomeIcon icon={faShoppingCart} title="Shopping cart" />
      <span>cart</span>
    </button>
  )

  const buttonContent =
    cartItemCount === 0 ? (
      <span>{CartButton}</span>
    ) : (
      <div className="indicator">
        <span className="indicator-item badge badge-secondary font-semibold">
          {cartItemCount}
        </span>
        {CartButton}
      </div>
    )

  // If targetSelector is provided and target element exists, use portal
  if (targetSelector && targetElement) {
    return createPortal(buttonContent, targetElement)
  }

  // Default behavior - render normally
  return buttonContent
}

export default ShoppingCartButton
